import React, { useCallback, useEffect, useMemo } from "react";
import { Table } from "@prestosports/commons-ui";
import Checkbox from "@material-ui/core/Checkbox";
import { Box } from "@material-ui/core";
import Tennis32 from "@carbon/icons-react/lib/tennis/32";
import { PlayerStatus } from "@prestosports/prestostats-core/Common";
import { usePlayersContext } from "../../../state/players/context";
import PlayerFormat from "../../../util/player-format";
import playerListTableStyles from "../../team/styles/PlayerListTable.styles";
import { useTennisTeamContext } from "../../../state/team-context/tennis-context";
import { useAppContext } from "../../../state/app/context";
import { useMatchesContext } from "../../../state/matches/context";
import { useEventContext } from "../../../state/events/context";

const playerListColumns = ({
	singleParticipants,
	doubleParticipants,
	toggleSingle,
	toggleDoubles,
	handleStarterToggle,
	classes
}) => {
	return [
		{
			label: "FIRST NAME",
			id: "firstName",
			sortId: "firstName",
			component: ({ data: player }) => player.firstName
		},
		{
			label: "LAST NAME",
			id: "lastName",
			sortId: "lastName",
			component: ({ data: player }) => player.lastName
		},
		{
			label: "YR.",
			id: "year",
			sortId: "year",
			component: ({ data: player }) => (player.year ? player.year : "")
		},
		{
			label: "NUMBER",
			sortId: "uniform",
			component: ({ data: player }) => {
				return player.uniform ? player.uniform : "-";
			}
		},
		{
			label: "NATIONAL RANKING",
			sortId: "nationalRanking",
			component: ({ data: player }) => {
				return player.nationalRanking ? player.nationalRanking : "-";
			}
		},
		{
			label: "REGIONAL RANKING",
			sortId: "regionalRanking",
			component: ({ data: player }) => {
				return player.regionalRanking ? player.regionalRanking : "-";
			}
		},
		{
			label: "CONFERENCE RANKING",
			sortId: "conferenceRanking",
			component: ({ data: player }) => {
				return player.conferenceRanking ? player.conferenceRanking : "-";
			}
		},
		{
			label: "STARTER",
			id: "starter",
			sortId: "starter",
			component: ({ data: player }) => (
				<Checkbox
					className={classes.disabledChkBox}
					disabled={player.isSaving}
					checked={player.starter}
					onChange={() => handleStarterToggle(player)}
				/>
			)
		},
		{
			label: "SINGLE",
			id: "single",
			sortId: "single",

			component: ({ data: player }) =>
				singleParticipants.some((singlePlayer) => singlePlayer === player.id) ? (
					<Tennis32 className={classes.icon} />
				) : (
					<Checkbox checked={player.single} onChange={() => toggleSingle(player)} />
				)
		},
		{
			label: "DOUBLES",
			id: "doubles",
			sortId: "doubles",
			component: ({ data: player }) =>
				doubleParticipants.some((doublePlayer) => doublePlayer === player.id) ? (
					<Tennis32 className={classes.icon} />
				) : (
					<Checkbox
						checked={player.doubles}
						onChange={() => toggleDoubles(player)}
					/>
				)
		}
	];
};

const PlayerList = ({
	team,
	onRowAction,
	onSelectionChanged,
	selectedPlayerIds,
	onPageChanged,
	currentPage,
	onSortChanged,
	sortField = "uniform",
	sortDirection = "desc",
	playerType = PlayerStatus.ACTIVE
}) => {
	const classes = playerListTableStyles();

	const {
		state: teamStatsState,
		toggleSingle,
		toggleDoubles
	} = useTennisTeamContext({
		team
	});

	const { state: playersState, savePlayer } = usePlayersContext();
	const { state: matchesState, getMatches } = useMatchesContext();
	const { state: eventState } = useEventContext();
	const { state: appContextState } = useAppContext();

	const single = teamStatsState.single() || [];
	const doubles = teamStatsState.doubles() || [];
	const players = playersState.players(team.id);
	const savingPlayerIds = playersState.savingPlayerIds();
	const currentEvent = eventState.currentEvent();
	const singleParticipants = matchesState.singleParticipants();
	const doubleParticipants = matchesState.doubleParticipants();
	const allParticipants = useMemo(
		() => singleParticipants.concat(doubleParticipants),
		[singleParticipants, doubleParticipants]
	);
	const playerManagement = appContextState.playerManagement();

	const hiddenUniforms = [
		PlayerFormat.TeamUniform,
		PlayerFormat.NotSpecifiedUniform
	];

	const getPlayerStatus = ({ playerStatus }) => {
		return playerStatus || PlayerStatus.ACTIVE;
	};

	const extendedPlayers = players
		.filter(
			(p) =>
				!hiddenUniforms.includes(p.uniform) && playerType === getPlayerStatus(p)
		)
		.map((player) => {
			return {
				...player,
				single: !!single.find((p) => p === player.id),
				doubles: !!doubles.find((p) => p === player.id),
				isSaving: savingPlayerIds.some((spId) => spId === player.id)
			};
		});

	useEffect(() => {
		if (currentEvent) getMatches(currentEvent.id);
	}, [currentEvent]);

	const selectionDisabled = useCallback(
		(row) => allParticipants.find((participant) => row.id === participant),
		[allParticipants]
	);

	const handleStarterToggle = (player) => {
		savePlayer({ ...player, starter: !player.starter });
	};

	const tableColumns = useMemo(
		() =>
			playerListColumns({
				singleParticipants,
				doubleParticipants,
				toggleSingle,
				toggleDoubles,
				handleStarterToggle,
				classes
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[singleParticipants, doubleParticipants, classes]
	);

	return (
		<Box className={classes.root}>
			<Table
				columns={tableColumns}
				rows={extendedPlayers}
				selectionDisabled={selectionDisabled}
				initialRowsPerPage={25}
				rowsPerPageOptionsEnabled={false}
				sortField={sortField}
				sortDirection={sortDirection}
				handleRequestSortCallback={onSortChanged}
				multiSelect={
					playerManagement ? { onSelect: onSelectionChanged, rowId: "id" } : {}
				}
				rowAction={playerManagement && onRowAction}
				handleChangePageCallback={onPageChanged}
				selectedRows={selectedPlayerIds}
				currentPage={currentPage}
			/>
		</Box>
	);
};

export default PlayerList;
